/**
 * APTypography Component
 *
 * A comprehensive typography component following Figma design system specifications.
 * Supports all typography sizes, weights, colors, and accessibility features.
 *
 * @example
 * ```tsx
 * <APTypography size="large" weight="600" as="h2">
 *   Welcome to AstroPay
 * </APTypography>
 *
 * <APTypography
 *   size="base"
 *   weight="400"
 *   color="muted"
 *   underline
 *   as="p"
 * >
 *   This is underlined text
 * </APTypography>
 *
 * <APTypography
 *   size="displayLarge"
 *   weight="600"
 *   as="h1"
 *   align="center"
 *   color="highlight"
 * >
 *   Hero Title
 * </APTypography>
 * ```
 */

import { createElement, forwardRef } from 'react';

import type {
  IAPTypographyProps,
  TTypographyColor,
  TTypographySize,
  TTypographyWeight,
} from './types';

import {
  getAlignClass,
  getColorClass,
  getElementSemantics,
  getLineClampStyles,
  getSizeClasses,
  getTransformClass,
  validateTypographyProps,
} from './utils';

import './styles.scss';

/**
 * APTypography - Production-ready typography component
 *
 * Follows AstroPay design system and accessibility guidelines.
 * Based on Figma typography specifications with full semantic support.
 */
export const APTypography = forwardRef<HTMLElement, IAPTypographyProps>(
  (
    {
      size = 'base',
      weight = '400',
      as = 'span',
      children,
      text,
      align = 'left',
      transform = 'none',
      color = 'base',
      underline = false,
      truncate = false,
      lineClamp,
      inline = false,
      className = '',
      style,
      'data-testid': testId,
      'aria-label': ariaLabel,
      'aria-level': ariaLevel,
      ...rest
    },
    ref
  ) => {
    // Validate props and log warnings in development
    if (process.env.NODE_ENV === 'development') {
      const validation = validateTypographyProps(as, size, weight, underline);
      if (!validation.isValid) {
        validation.warnings.forEach((warning) => {
          console.warn(`[APTypography]: ${warning}`);
        });
      }
    }

    // Get semantic attributes for accessibility
    const semanticAttributes = getElementSemantics(as, size);
    const finalAriaLevel = ariaLevel || semanticAttributes['aria-level'];

    // Construct class names using BEM methodology
    const baseClasses = 'ap-typography';
    const sizeClasses = getSizeClasses(size, weight);
    const colorClasses = getColorClass(color);
    const alignClasses = align !== 'left' ? getAlignClass(align) : '';
    const transformClasses = getTransformClass(transform);

    const modifierClasses = [
      underline && 'ap-typography--underline',
      truncate && 'ap-typography--truncate',
      lineClamp !== undefined && 'ap-typography--line-clamp',
      inline && 'ap-typography--inline',
    ].filter(Boolean);

    const typographyClasses = [
      baseClasses,
      sizeClasses,
      colorClasses,
      alignClasses,
      transformClasses,
      ...modifierClasses,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Combine styles
    const combinedStyles: React.CSSProperties = {
      ...style,
      ...(lineClamp !== undefined ? getLineClampStyles(lineClamp) : {}),
    };

    // Create element props
    const elementProps = {
      ref,
      className: typographyClasses,
      style: Object.keys(combinedStyles).length > 0 ? combinedStyles : undefined,
      'data-testid': testId,
      'aria-label': ariaLabel,
      'aria-level': finalAriaLevel,
      role: semanticAttributes.role,
      ...rest,
    };

    // Dynamically render the specified element using createElement
    return createElement(as, elementProps, text ?? children);
  }
);

APTypography.displayName = 'APTypography';

// Export types for external use
export type { IAPTypographyProps, TTypographySize, TTypographyWeight, TTypographyColor };
export default APTypography;
